// Pipeline detail page. Tabs: Config / Runs / Artifacts / Knowledge Graph /
// Changelog. Config tab edits a draft locally and PUTs the whole payload on
// save. Tracks a `dirty` flag derived from comparing the draft to the
// baseline snapshot — used to gate the Run button (mirrors the metaphysics
// view). KG concerns (schema editor + sync trigger + version list) live in
// their own tab now — see PipelineKgTab.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';

import { useAuth } from '../../../auth/AuthProvider';
import { BackButton } from '../../../components/common/BackButton';
import { EmptyState } from '../../../components/common/EmptyState';
import { PageHeader } from '../../../components/common/PageHeader';
import { Spinner } from '../../../components/common/Spinner';
import { BlueprintsPicker } from '../../../components/pipelines/BlueprintsPicker';
import { EnvVarsEditor } from '../../../components/pipelines/EnvVarsEditor';
import { KnowledgeSourcesEditor } from '../../../components/pipelines/KnowledgeSourcesEditor';
import { OperationalSourcesEditor } from '../../../components/pipelines/OperationalSourcesEditor';
import { PipelineArtifacts } from '../../../components/pipelines/PipelineArtifacts';
import { PipelineChangelog } from '../../../components/pipelines/PipelineChangelog';
import { PipelineKgTab } from '../../../components/pipelines/PipelineKgTab';
import { PipelineRuns } from '../../../components/pipelines/PipelineRuns';
import { PostRunAutomation } from '../../../components/pipelines/PostRunAutomation';
import { PipelinePreviewModal } from '../../../components/pipelines/PreviewModal';
import { routes } from '../../../lib/constants';
import {
  useMetaphysicsPipelines,
  type ICortexEvaluationSummary,
  type IOperationalSource,
  type IPipeline,
  type IPipelineSourceEntry,
  type IPipelineUpdateInput,
} from '../../../lib/metaphysics-pipelines';
import { guardSuperuser } from '../index';

export const Route = createFileRoute('/internal/pipelines/$pipelineId')({
  beforeLoad: () => guardSuperuser(),
  component: PipelineDetailPage,
});

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };

interface IDraft {
  name: string;
  blueprintIds: string[];
  knowledgeSources: IPipelineSourceEntry[];
  operationalSources: IOperationalSource[];
  autoPromoteEnabled: boolean;
  autoPromotePrompt: string;
  autoSyncEnabled: boolean;
  envVars: Record<string, string>;
}

function pipelineToDraft(p: IPipeline): IDraft {
  return {
    name: p.name,
    blueprintIds: (p.blueprints ?? []).map((b) => b.id),
    knowledgeSources: (p.knowledgeSources ?? []).map((s) => ({
      sourceId: s.sourceId,
      mountFolder: s.mountFolder ?? null,
      source: s.source,
    })),
    operationalSources: [...(p.operationalSources ?? [])],
    autoPromoteEnabled: p.autoPromoteEnabled ?? false,
    autoPromotePrompt: p.autoPromotePrompt ?? '',
    autoSyncEnabled: p.autoSyncEnabled ?? false,
    envVars: p.envVars ?? {},
  };
}

function draftToUpdate(d: IDraft): IPipelineUpdateInput {
  return {
    name: d.name,
    blueprints: d.blueprintIds,
    knowledgeSources: d.knowledgeSources.map((s) => ({
      sourceId: s.sourceId,
      mountFolder: s.mountFolder ?? null,
    })),
    operationalSources: d.operationalSources,
    autoPromoteEnabled: d.autoPromoteEnabled,
    autoPromotePrompt: d.autoPromotePrompt.trim() || null,
    /** Server-side rule: auto-sync requires auto-promote. */
    autoSyncEnabled: d.autoPromoteEnabled && d.autoSyncEnabled,
    envVars: d.envVars,
  };
}

type ITab = 'config' | 'runs' | 'artifacts' | 'kg' | 'changelog';

function PipelineDetailPage() {
  const { pipelineId } = Route.useParams();
  const mp = useMetaphysicsPipelines();
  const { token } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();

  const pipeline = useQuery({
    queryKey: ['mp', 'pipeline', pipelineId],
    queryFn: () => mp.getPipeline(pipelineId),
  });

  // Lookup data shared across the form sub-editors.
  const sources = useQuery({ queryKey: ['mp', 'sources'], queryFn: () => mp.listSources() });
  const blueprints = useQuery({
    queryKey: ['mp', 'blueprints'],
    queryFn: () => mp.listBlueprints(),
  });
  const datasets = useQuery({
    queryKey: ['mp', 'datasets'],
    queryFn: () => mp.listDatasets(),
  });
  const workflows = useQuery({
    queryKey: ['mp', 'workflows'],
    queryFn: () => mp.listWorkflows(),
  });
  const allPipelines = useQuery({
    queryKey: ['mp', 'pipelines'],
    queryFn: () => mp.listPipelines(),
  });
  // Cortex evaluations for the new `evaluation` operational-source kind.
  // Calls cortex/api directly with the user's bearer token (user-token
  // path — same JWT, same realm). Not filtered by workflowId since the
  // metaphysics pipeline itself has no workflowId field; the user picks
  // a specific evaluation from the dropdown.
  const cortexEvaluations = useQuery({
    queryKey: ['cortex', 'evaluations'],
    enabled: !!token,
    queryFn: async (): Promise<ICortexEvaluationSummary[]> => {
      const baseUrl = (
        import.meta.env.VITE_API_URL ?? 'https://api.cortex-dev.formant.ai/api'
      ).replace(/\/$/, '');
      const res = await fetch(`${baseUrl}/v1/evaluations?limit=200`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (!res.ok) {
        // Don't surface as a hard error — the picker just renders an empty
        // dropdown if cortex/api is unreachable / 401s on this user.
        return [];
      }
      const body = (await res.json()) as { items?: ICortexEvaluationSummary[] };
      return body.items ?? [];
    },
  });

  const [tab, setTab] = useState<ITab>('config');
  const [draft, setDraft] = useState<IDraft | null>(null);
  const [baseline, setBaseline] = useState<string>('');

  // Hydrate draft once pipeline data lands. Hydrate sources too — the
  // server returns knowledgeSources without the hydrated `source` object.
  useEffect(() => {
    if (!pipeline.data || draft) return;
    const d = pipelineToDraft(pipeline.data);
    if (sources.data) {
      d.knowledgeSources = d.knowledgeSources.map((e) => ({
        ...e,
        source: e.source ?? sources.data.find((s) => s.id === e.sourceId),
      }));
    }
    setDraft(d);
    setBaseline(JSON.stringify(d));
  }, [pipeline.data, sources.data, draft]);

  const dirty = !!draft && baseline !== '' && JSON.stringify(draft) !== baseline;

  const save = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('Draft not ready');
      return mp.updatePipeline(pipelineId, draftToUpdate(draft));
    },
    onSuccess: async (p) => {
      const d = pipelineToDraft(p);
      d.knowledgeSources = d.knowledgeSources.map((e) => ({
        ...e,
        source: e.source ?? sources.data?.find((s) => s.id === e.sourceId),
      }));
      setDraft(d);
      setBaseline(JSON.stringify(d));
      try {
        await qc.invalidateQueries({ queryKey: ['mp', 'pipelines'] });
        await qc.invalidateQueries({ queryKey: ['mp', 'pipeline', pipelineId] });
      } catch (err) {
        console.error(err);
      }
    },
  });

  const remove = useMutation({
    mutationFn: () => mp.deletePipeline(pipelineId),
    onSuccess: async () => {
      try {
        await qc.invalidateQueries({ queryKey: ['mp', 'pipelines'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.INTERNAL_PIPELINES });
      } catch (err) {
        console.error(err);
      }
    },
  });

  const targetArtifacts = useMemo(() => {
    if (!pipeline.data) return [];
    return (pipeline.data.blueprints ?? []).flatMap((b) => b.targetArtifacts ?? []);
  }, [pipeline.data]);

  if (pipeline.isLoading || !draft) return <Spinner />;
  if (pipeline.error)
    return <div className="alert alert--danger">{(pipeline.error as Error).message}</div>;
  if (!pipeline.data) return <EmptyState title="Not found" />;

  const setD = (patch: Partial<IDraft>) =>
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  return (
    <>
      <PageHeader
        title={pipeline.data.name}
        actions={
          <>
            <BackButton to={routes.INTERNAL_PIPELINES} label="Pipelines" />
            <button
              type="button"
              className="btn btn--destructive btn--sm"
              disabled={remove.isPending}
              onClick={() => {
                if (confirm(`Delete pipeline "${pipeline.data?.name}"?`)) remove.mutate();
              }}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </>
        }
      />

      <nav className="tabs" style={{ marginBottom: 'var(--sp-3)' }}>
        {(['config', 'runs', 'artifacts', 'kg', 'changelog'] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={`tab${tab === t ? ' is-active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'config'
              ? 'Config'
              : t === 'runs'
                ? 'Runs'
                : t === 'artifacts'
                  ? 'Artifacts'
                  : t === 'kg'
                    ? 'Knowledge Graph'
                    : 'Changelog'}
          </button>
        ))}
      </nav>

      {tab === 'config' ? (
        <div className="card" style={{ maxWidth: 980 }}>
          <div className="card-body">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (dirty) save.mutate();
              }}
              style={stack3}
            >
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  className="input"
                  required={true}
                  value={draft.name}
                  onChange={(e) => setD({ name: e.target.value })}
                />
              </label>

              <BlueprintsPicker
                blueprints={blueprints.data ?? []}
                value={draft.blueprintIds}
                onChange={(blueprintIds) => setD({ blueprintIds })}
              />

              <KnowledgeSourcesEditor
                entries={draft.knowledgeSources}
                sources={sources.data ?? []}
                onChange={(knowledgeSources) => setD({ knowledgeSources })}
              />

              <OperationalSourcesEditor
                entries={draft.operationalSources}
                datasets={datasets.data ?? []}
                pipelines={allPipelines.data ?? []}
                workflows={workflows.data ?? []}
                evaluations={cortexEvaluations.data ?? []}
                currentPipelineId={pipelineId}
                onChange={(operationalSources) => setD({ operationalSources })}
              />

              <PostRunAutomation
                autoPromoteEnabled={draft.autoPromoteEnabled}
                autoPromotePrompt={draft.autoPromotePrompt}
                autoSyncEnabled={draft.autoSyncEnabled}
                onChange={(patch) => setD(patch)}
              />

              <EnvVarsEditor value={draft.envVars} onChange={(envVars) => setD({ envVars })} />

              {save.error ? (
                <div className="alert alert--danger">{(save.error as Error).message}</div>
              ) : null}
              {remove.error ? (
                <div className="alert alert--danger">{(remove.error as Error).message}</div>
              ) : null}

              <div
                style={{
                  display: 'flex',
                  gap: 'var(--sp-2)',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                {dirty ? <span className="t-xs t-muted">Unsaved changes</span> : null}
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={!dirty || !draft.name.trim() || save.isPending}
                >
                  {save.isPending ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {tab === 'runs' ? (
        <PipelineRuns
          pipelineId={pipelineId}
          targetArtifacts={targetArtifacts}
          formDirty={dirty}
          autoPromoteEnabled={draft.autoPromoteEnabled}
        />
      ) : null}

      {tab === 'artifacts' ? (
        <PipelineArtifacts pipelineId={pipelineId} blueprints={pipeline.data.blueprints ?? []} />
      ) : null}

      {tab === 'kg' ? (
        <PipelineKgTab
          pipelineId={pipelineId}
          activeSchemaId={pipeline.data.activeExtractionSchemaId ?? null}
        />
      ) : null}

      {tab === 'changelog' ? <PipelineChangelog pipelineId={pipelineId} /> : null}

      <PipelinePreviewModal />
    </>
  );
}
